// UKInsights.js — Full UK Engineering Intelligence Dashboard
/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  SafeAreaView, View, Text, StyleSheet, ScrollView, TouchableOpacity,
  Dimensions, ActivityIndicator, TextInput, Share, Alert, FlatList,
  Image, RefreshControl, Platform, Animated, Modal, Linking,
} from 'react-native';
import {
  Ionicons, MaterialCommunityIcons, FontAwesome5, MaterialIcons, Entypo,
} from '@expo/vector-icons';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import { NewsAlertBadge, NewsAlertSystem } from './NewsAlertSystem';
import NewsTicker from './NewsTicker';

const { width, height } = Dimensions.get('window');

const ANTHROPIC_API_KEY = 'YOUR_ANTHROPIC_API_KEY';
const GUARDIAN_KEY      = '96d3065f-e31b-47fe-80d5-96a2664509e9';
const REED_KEY          = 'c49ba09d-1dbc-4854-b73b-88f0fc74e7d2';

// ─────────────────────────────────────────────────────────────────────────────
// FALLBACK DATA
// ─────────────────────────────────────────────────────────────────────────────
const FB = {
  jobMarket: { totalJobs: 15247, growthRate: 12.5, avgTimeToFill: 28, topSectors: [
    { name: 'Software Engineering', jobs: 4200, growth: 15, color: '#0e4e96' },
    { name: 'Renewable Energy',     jobs: 2800, growth: 22, color: '#10b981' },
    { name: 'Civil Engineering',    jobs: 1950, growth: 8,  color: '#19abb5' },
    { name: 'Mechanical Eng.',      jobs: 1820, growth: 6,  color: '#fdb913' },
    { name: 'Electrical Eng.',      jobs: 1650, growth: 9,  color: '#8b5cf6' },
  ], recentJobs: [
    { title: 'Senior Software Engineer',           company: 'BAE Systems',      location: 'Manchester',  salary: '£75,000', url: 'https://www.gov.uk/find-a-job' },
    { title: 'Civil Engineering Project Manager',  company: 'Atkins',           location: 'London',      salary: '£65,000', url: 'https://www.gov.uk/find-a-job' },
    { title: 'Renewable Energy Engineer',          company: 'National Grid',    location: 'Edinburgh',   salary: '£55,000', url: 'https://www.gov.uk/find-a-job' },
    { title: 'Aerospace Systems Engineer',         company: 'Rolls-Royce',      location: 'Derby',       salary: '£62,000', url: 'https://www.gov.uk/find-a-job' },
    { title: 'Process & Chemical Engineer',        company: 'BP',               location: 'Aberdeen',    salary: '£58,000', url: 'https://www.gov.uk/find-a-job' },
  ] },
  salaries: { average: 52500, growth: 4.2, top10: 85000, byDiscipline: {
    Civil: 45000, Mechanical: 48000, Electrical: 52000, Chemical: 55000, Software: 65000, Aerospace: 58000, Nuclear: 70000,
  } },
  skills: [
    { name: 'AI & Machine Learning', demand: 25, growth: 45, color: '#0e4e96', icon: 'brain' },
    { name: 'Renewable Energy',       demand: 22, growth: 38, color: '#10b981', icon: 'solar-power' },
    { name: 'Data Analysis',          demand: 18, growth: 32, color: '#fdb913', icon: 'chart-bar' },
    { name: 'Automation/Robotics',    demand: 15, growth: 28, color: '#8b5cf6', icon: 'robot-industrial' },
    { name: 'Cybersecurity',          demand: 12, growth: 25, color: '#ef4444', icon: 'shield-check' },
    { name: 'BIM / Digital Twins',    demand: 8,  growth: 20, color: '#f59e0b', icon: 'home-city' },
    { name: 'Cloud Architecture',     demand: 10, growth: 35, color: '#19abb5', icon: 'cloud-outline' },
  ],
  regional: [
    { region: 'London',      jobs: 5200, avgSalary: 62500, growth: 7.2, topSector: 'Software Eng.' },
    { region: 'Manchester',  jobs: 2800, avgSalary: 52000, growth: 5.8, topSector: 'Mechanical Eng.' },
    { region: 'Birmingham',  jobs: 2400, avgSalary: 48500, growth: 4.9, topSector: 'Manufacturing' },
    { region: 'Edinburgh',   jobs: 1900, avgSalary: 54000, growth: 6.3, topSector: 'Renewable Energy' },
    { region: 'Bristol',     jobs: 1700, avgSalary: 51000, growth: 5.5, topSector: 'Aerospace' },
    { region: 'Leeds',       jobs: 1400, avgSalary: 47500, growth: 4.1, topSector: 'Civil Eng.' },
    { region: 'Cambridge',   jobs: 1200, avgSalary: 58000, growth: 6.8, topSector: 'Software Eng.' },
  ],
  stats: { totalEngineers: 650000, charteredEngineers: 180000, newRegistrations: 25000, employmentRate: 97.2 },
  education: [
    { name: 'University of Cambridge',        rank: '#1 UK', enrolled: '2,400', employment: '99%' },
    { name: 'Imperial College London',         rank: '#2 UK', enrolled: '3,100', employment: '98%' },
    { name: 'University of Oxford',            rank: '#3 UK', enrolled: '1,800', employment: '99%' },
    { name: 'University of Edinburgh',         rank: '#4 UK', enrolled: '2,800', employment: '97%' },
    { name: 'University of Manchester',        rank: '#5 UK', enrolled: '3,500', employment: '96%' },
    { name: 'University College London (UCL)', rank: '#6 UK', enrolled: '2,900', employment: '97%' },
  ],
};

// ─────────────────────────────────────────────────────────────────────────────
// DATA SERVICES
// ─────────────────────────────────────────────────────────────────────────────
const fetchNews = async () => {
  try {
    const res  = await fetch(`https://content.guardianapis.com/search?q=engineering+UK&show-fields=trailText,thumbnail,byline&page-size=8&order-by=newest&api-key=${GUARDIAN_KEY}`);
    const json = await res.json();
    return (json.response?.results || []).map(a => ({
      id: a.id, title: a.webTitle, snippet: a.fields?.trailText || '',
      source: 'The Guardian', category: a.sectionName, publishDate: new Date(a.webPublicationDate),
      imageUrl: a.fields?.thumbnail, readTime: 4, views: Math.floor(Math.random() * 3000),
      likes: Math.floor(Math.random() * 150), shares: Math.floor(Math.random() * 60),
    }));
  } catch {
    return [
      { id: 'f1', title: 'UK Engineering Sector Sees Record Growth in 2025', snippet: 'The engineering industry has shown remarkable resilience with a 15% increase in hiring.', source: 'Engineering UK', category: 'Industry', publishDate: new Date(), readTime: 5, views: 1200, likes: 89, shares: 34 },
      { id: 'f2', title: 'Government Announces £2.3bn Investment in Green Engineering', snippet: 'New funding aims to create 45,000 engineering jobs in renewable energy sectors.', source: 'BBC News', category: 'Policy', publishDate: new Date(), readTime: 4, views: 3400, likes: 210, shares: 98 },
      { id: 'f3', title: 'Demand for AI and Robotics Engineers Increases by 35%', snippet: 'Companies are rapidly adopting automation, creating unprecedented demand.', source: 'TechCrunch', category: 'Technology', publishDate: new Date(), readTime: 6, views: 2100, likes: 156, shares: 67 },
    ];
  }
};

const fetchAIInsights = async (data) => {
  try {
    const res = await fetch('https://api.anthropic.com/v1/messages', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        model: 'claude-sonnet-4-20250514',
        max_tokens: 1000,
        messages: [{
          role: 'user',
          content: `You are a UK engineering careers expert. Based on this current market data:
- Total engineering jobs: ${data.totalJobs?.toLocaleString()}
- Market growth: ${data.growthRate}% YoY  
- Average salary: £${data.avgSalary?.toLocaleString()}
- Top growing sector: Renewable Energy (+22%)
- Top skill demand: AI/ML (+45%)

Provide 4 concise, actionable insights for UK engineers in 2025. Each insight should be 1-2 sentences.
Format as JSON array: [{"icon": "MaterialCommunityIcons icon name", "color": "#hexcolor", "title": "short title", "text": "insight text"}]`
        }],
      }),
    });
    const d = await res.json();
    const t = d.content?.[0]?.text || '';
    const c = t.replace(/```json|```/g, '').trim();
    return JSON.parse(c);
  } catch {
    return [
      { icon: 'trending-up',    color: '#10b981', title: 'High Demand',       text: 'Engineering jobs are growing 3x faster than the UK average — now is the best time to job hunt or negotiate a pay rise.' },
      { icon: 'brain',          color: '#0e4e96', title: 'AI Skills Premium', text: 'Engineers with AI/ML skills are commanding 20-35% salary premiums. Consider online courses to stay competitive.' },
      { icon: 'solar-power',    color: '#fdb913', title: 'Green Boom',        text: 'Renewable energy engineering roles are growing at 22% — the fastest sector. Upskilling in energy storage or solar is valuable.' },
      { icon: 'map-marker',     color: '#8b5cf6', title: 'Go North',          text: 'Northern cities like Manchester, Leeds and Edinburgh offer 95% of London salaries with significantly lower cost of living.' },
    ];
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// REUSABLE COMPONENTS
// ─────────────────────────────────────────────────────────────────────────────
const DataCard = ({ title, value, change, icon, color, loading, onPress }) => (
  <TouchableOpacity style={[styles.dataCard, { borderLeftColor: color }]} onPress={onPress} activeOpacity={0.8}>
    {loading ? (
      <ActivityIndicator size={20} color={color} />
    ) : (
      <>
        <View style={styles.dataCardTop}>
          <View style={[styles.dataCardIcon, { backgroundColor: color + '22' }]}>
            <MaterialCommunityIcons name={icon} size={18} color={color} />
          </View>
          {change !== undefined && (
            <View style={[styles.changeBadge, { backgroundColor: change > 0 ? '#10b98118' : '#ef444418' }]}>
              <MaterialCommunityIcons name={change > 0 ? 'trending-up' : 'trending-down'} size={11} color={change > 0 ? '#10b981' : '#ef4444'} />
              <Text style={[styles.changeText, { color: change > 0 ? '#10b981' : '#ef4444' }]}>{change > 0 ? '+' : ''}{change}%</Text>
            </View>
          )}
        </View>
        <Text style={styles.dataCardValue}>{value}</Text>
        <Text style={styles.dataCardTitle}>{title}</Text>
      </>
    )}
  </TouchableOpacity>
);

const LiveDot = ({ live = true }) => {
  const anim = useRef(new Animated.Value(1)).current;
  useEffect(() => {
    if (!live) return;
    Animated.loop(Animated.sequence([
      Animated.timing(anim, { toValue: 1.6, duration: 700, useNativeDriver: true }),
      Animated.timing(anim, { toValue: 1,   duration: 700, useNativeDriver: true }),
    ])).start();
  }, [live]);
  return <Animated.View style={[styles.liveDot, { backgroundColor: live ? '#10b981' : '#f59e0b', transform: [{ scale: anim }] }]} />;
};

const SectorBar = ({ sector, maxJobs }) => {
  const anim = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    Animated.timing(anim, { toValue: (sector.jobs / maxJobs), duration: 800, useNativeDriver: false }).start();
  }, [sector.jobs]);
  return (
    <View style={styles.sectorRow}>
      <View style={styles.sectorInfo}>
        <Text style={styles.sectorName}>{sector.name}</Text>
        <Text style={styles.sectorJobs}>{sector.jobs.toLocaleString()} jobs</Text>
      </View>
      <View style={styles.sectorBarTrack}>
        <Animated.View style={[styles.sectorBarFill, { width: anim.interpolate({ inputRange: [0, 1], outputRange: ['0%', '100%'] }), backgroundColor: sector.color || '#0e4e96' }]} />
      </View>
      <View style={[styles.growthBadge, { backgroundColor: sector.growth > 10 ? '#10b98120' : '#f59e0b20' }]}>
        <Text style={[styles.growthText, { color: sector.growth > 10 ? '#10b981' : '#f59e0b' }]}>+{sector.growth}%</Text>
      </View>
    </View>
  );
};

const formatDate = (d) => {
  if (!d) return '';
  const diff = Date.now() - new Date(d).getTime();
  const h = Math.floor(diff / 3600000);
  if (h < 1)  return 'Just now';
  if (h < 24) return `${h}h ago`;
  return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN SCREEN
// ─────────────────────────────────────────────────────────────────────────────
export default function UKInsightsScreen() {
  const navigation = useNavigation();

  const [activeTab,    setActiveTab]    = useState('overview');
  const [loading,      setLoading]      = useState(true);
  const [refreshing,   setRefreshing]   = useState(false);
  const [isLive,       setIsLive]       = useState(true);
  const [lastUpdated,  setLastUpdated]  = useState('just now');
  const [searchQuery,  setSearchQuery]  = useState('');

  const [showAlerts,   setShowAlerts]   = useState(false);
  const [alertCount,   setAlertCount]   = useState(2);

  const [data, setData] = useState({
    jobMarket: FB.jobMarket, salaries: FB.salaries,
    news: [], skills: FB.skills, regional: FB.regional,
    stats: FB.stats, education: FB.education,
  });
  const [aiInsights,    setAiInsights]    = useState([]);
  const [aiLoading,     setAiLoading]     = useState(false);
  const [savedInsights, setSavedInsights] = useState(new Set());
  const [selectedRegion, setSelectedRegion] = useState(null);

  const tabs = [
    { id: 'overview',   label: 'Overview',   icon: 'view-dashboard-outline' },
    { id: 'jobs',       label: 'Jobs',       icon: 'briefcase-outline' },
    { id: 'salaries',   label: 'Salaries',   icon: 'currency-gbp' },
    { id: 'skills',     label: 'Skills',     icon: 'brain' },
    { id: 'education',  label: 'Education',  icon: 'school-outline' },
    { id: 'regional',   label: 'Regional',   icon: 'map-marker-radius' },
    { id: 'news',       label: 'News',       icon: 'newspaper-variant' },
    { id: 'ai',         label: 'AI Insights',icon: 'robot-outline' },
  ];

  // ── Load data ─────────────────────────────────────────────────────────────
  const loadAllData = useCallback(async () => {
    try {
      const news = await fetchNews();
      setData(prev => ({ ...prev, news }));
      setIsLive(true);
      setLastUpdated('just now');
      const breaking = news.filter(n => n.title?.toLowerCase().includes('breaking') || n.priority === 'breaking');
      setAlertCount(Math.max(2, breaking.length));
    } catch {
      setIsLive(false);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  const loadAIInsights = async () => {
    if (aiInsights.length || aiLoading) return;
    setAiLoading(true);
    const res = await fetchAIInsights({
      totalJobs:  data.jobMarket.totalJobs,
      growthRate: data.jobMarket.growthRate,
      avgSalary:  data.salaries.average,
    });
    setAiInsights(res);
    setAiLoading(false);
  };

  useEffect(() => { loadAllData(); const t = setInterval(loadAllData, 5 * 60 * 1000); return () => clearInterval(t); }, []);
  useFocusEffect(useCallback(() => { loadAllData(); }, []));

  useEffect(() => { if (activeTab === 'ai' && !aiInsights.length) loadAIInsights(); }, [activeTab]);

  const onRefresh = () => { setRefreshing(true); loadAllData(); };

  // ── Handlers ──────────────────────────────────────────────────────────────
  const toggleSaved = (key) => {
    setSavedInsights(prev => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });
  };

  const handleShare = async (title, desc) => {
    try { await Share.share({ message: `${title}: ${desc} — JOBENG UK Insights`, title }); }
    catch { Alert.alert('Error', 'Could not share'); }
  };

  const openUrl = (url) => Linking.openURL(url).catch(() => Alert.alert('Error', 'Could not open link'));

  // ── Search filter ─────────────────────────────────────────────────────────
  const filteredNews = data.news.filter(n => !searchQuery || n.title?.toLowerCase().includes(searchQuery.toLowerCase()) || n.snippet?.toLowerCase().includes(searchQuery.toLowerCase()));

  // ─────────────────────────────────────────────────────────────────────────
  // TAB: OVERVIEW
  // ─────────────────────────────────────────────────────────────────────────
  const renderOverview = () => (
    <ScrollView showsVerticalScrollIndicator={false} refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor="#0e4e96" />}>
      <NewsTicker items={data.news.slice(0, 8)} onPress={art => navigation.navigate('NewsDetail', { newsItem: art })} />

      {/* Live status */}
      <View style={styles.liveBar}>
        <LiveDot live={isLive} />
        <Text style={styles.liveText}>{isLive ? 'LIVE' : 'OFFLINE'} · Updated {lastUpdated}</Text>
        <TouchableOpacity onPress={onRefresh} style={styles.refreshBtn}>
          <MaterialCommunityIcons name="refresh" size={16} color="#0e4e96" />
        </TouchableOpacity>
      </View>

      {/* KPI cards */}
      <View style={styles.kpiGrid}>
        <DataCard title="Job Openings"    value={data.jobMarket.totalJobs.toLocaleString()}       change={data.jobMarket.growthRate}   icon="briefcase-outline"  color="#0e4e96"  loading={loading} onPress={() => setActiveTab('jobs')} />
        <DataCard title="Avg. Salary"     value={`£${data.salaries.average.toLocaleString()}`}    change={data.salaries.growth}        icon="currency-gbp"       color="#19abb5"  loading={loading} onPress={() => setActiveTab('salaries')} />
        <DataCard title="Market Growth"   value={`${data.jobMarket.growthRate}%`}                 icon="trending-up"                   color="#10b981"  loading={loading} onPress={() => setActiveTab('jobs')} />
        <DataCard title="Employment Rate" value={`${data.stats.employmentRate}%`}                 icon="account-check"                 color="#fdb913"  loading={loading} onPress={() => setActiveTab('jobs')} />
      </View>

      {/* Quick actions */}
      <View style={styles.quickActionsGrid}>
        {[
          { icon: 'briefcase-search',      color: '#0e4e96', label: 'Find Jobs',       onPress: () => openUrl('https://www.gov.uk/find-a-job') },
          { icon: 'currency-gbp',          color: '#19abb5', label: 'Salary Tool',     onPress: () => navigation.navigate('SalaryComparison') },
          { icon: 'bell-ring-outline',     color: '#ef4444', label: 'Job Alerts',      onPress: () => setShowAlerts(true) },
          { icon: 'robot-outline',         color: '#8b5cf6', label: 'AI Insights',     onPress: () => setActiveTab('ai') },
          { icon: 'map-marker-outline',    color: '#f59e0b', label: 'Regional Map',    onPress: () => setActiveTab('regional') },
          { icon: 'share-variant-outline', color: '#10b981', label: 'Share Report',    onPress: () => handleShare('UK Engineering Market', `${data.jobMarket.totalJobs.toLocaleString()} jobs · £${data.salaries.average.toLocaleString()} avg · ${data.jobMarket.growthRate}% growth`) },
        ].map(a => (
          <TouchableOpacity key={a.label} style={styles.quickAction} onPress={a.onPress}>
            <View style={[styles.quickActionIcon, { backgroundColor: a.color + '18' }]}>
              <MaterialCommunityIcons name={a.icon} size={22} color={a.color} />
            </View>
            <Text style={styles.quickActionLabel}>{a.label}</Text>
          </TouchableOpacity>
        ))}
      </View>

      {/* Top sectors */}
      <View style={styles.section}>
        <View style={styles.sectionHeader}>
          <Text style={styles.sectionTitle}>Top Hiring Sectors</Text>
          <TouchableOpacity onPress={() => setActiveTab('jobs')}>
            <Text style={styles.seeAll}>See All →</Text>
          </TouchableOpacity>
        </View>
        {data.jobMarket.topSectors.slice(0, 4).map(s => (
          <SectorBar key={s.name} sector={s} maxJobs={Math.max(...data.jobMarket.topSectors.map(x => x.jobs))} />
        ))}
      </View>

      {/* Top skills */}
      <View style={styles.section}>
        <View style={styles.sectionHeader}>
          <Text style={styles.sectionTitle}>In-Demand Skills</Text>
          <TouchableOpacity onPress={() => setActiveTab('skills')}>
            <Text style={styles.seeAll}>See All →</Text>
          </TouchableOpacity>
        </View>
        {data.skills.slice(0, 4).map((sk, i) => (
          <View key={i} style={styles.skillRow}>
            <View style={[styles.skillIcon, { backgroundColor: sk.color + '22' }]}>
              <MaterialCommunityIcons name={sk.icon} size={18} color={sk.color} />
            </View>
            <View style={{ flex: 1 }}>
              <Text style={styles.skillName}>{sk.name}</Text>
              <View style={styles.skillBarTrack}>
                <View style={[styles.skillBarFill, { width: `${sk.demand}%`, backgroundColor: sk.color }]} />
              </View>
            </View>
            <View style={{ alignItems: 'flex-end' }}>
              <Text style={styles.skillDemand}>{sk.demand}%</Text>
              <Text style={[styles.skillGrowth, { color: '#10b981' }]}>+{sk.growth}%</Text>
            </View>
          </View>
        ))}
      </View>

      {/* Latest news */}
      <View style={styles.section}>
        <View style={styles.sectionHeader}>
          <Text style={styles.sectionTitle}>Latest News</Text>
          <TouchableOpacity onPress={() => navigation.navigate('NewsList')}>
            <Text style={styles.seeAll}>All News →</Text>
          </TouchableOpacity>
        </View>
        {data.news.slice(0, 3).map((art, i) => (
          <TouchableOpacity key={art.id || i} style={styles.newsCard} onPress={() => navigation.navigate('NewsDetail', { newsItem: art })}>
            <View style={styles.newsCardContent}>
              <View style={styles.newsCardMeta}>
                <Text style={styles.newsCardSource}>{art.source}</Text>
                <Text style={styles.newsCardDate}>{formatDate(art.publishDate)}</Text>
              </View>
              <Text style={styles.newsCardTitle} numberOfLines={2}>{art.title}</Text>
              <Text style={styles.newsCardSnippet} numberOfLines={2}>{art.snippet}</Text>
            </View>
            {art.imageUrl && <Image source={{ uri: art.imageUrl }} style={styles.newsCardThumb} />}
          </TouchableOpacity>
        ))}
      </View>

      {/* Regional top 3 */}
      <View style={styles.section}>
        <View style={styles.sectionHeader}>
          <Text style={styles.sectionTitle}>Top Regions</Text>
          <TouchableOpacity onPress={() => setActiveTab('regional')}>
            <Text style={styles.seeAll}>Map View →</Text>
          </TouchableOpacity>
        </View>
        <ScrollView horizontal showsHorizontalScrollIndicator={false}>
          {data.regional.slice(0, 5).map((r, i) => (
            <View key={r.region} style={styles.regionCard}>
              <Text style={styles.regionName}>{r.region}</Text>
              <Text style={styles.regionJobs}>{r.jobs.toLocaleString()} jobs</Text>
              <Text style={styles.regionSalary}>£{r.avgSalary.toLocaleString()} avg</Text>
              <View style={[styles.regionGrowth, { backgroundColor: '#10b98120' }]}>
                <Text style={[styles.regionGrowthText, { color: '#10b981' }]}>+{r.growth}%</Text>
              </View>
              <Text style={styles.regionSector}>{r.topSector}</Text>
            </View>
          ))}
        </ScrollView>
      </View>

      <View style={{ height: 30 }} />
    </ScrollView>
  );

  // ─────────────────────────────────────────────────────────────────────────
  // TAB: JOBS
  // ─────────────────────────────────────────────────────────────────────────
  const renderJobs = () => (
    <ScrollView showsVerticalScrollIndicator={false} refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor="#0e4e96" />}>
      <View style={styles.tabHeader}>
        <Text style={styles.tabTitle}>UK Engineering Job Market</Text>
        <Text style={styles.tabSubtitle}>Live data · Updated {lastUpdated}</Text>
      </View>

      <View style={styles.kpiStrip}>
        {[
          { label: 'Total Openings',  value: data.jobMarket.totalJobs.toLocaleString(),  color: '#0e4e96', icon: 'briefcase-outline' },
          { label: 'YoY Growth',      value: `+${data.jobMarket.growthRate}%`,            color: '#10b981', icon: 'trending-up' },
          { label: 'Days to Fill',    value: `${data.jobMarket.avgTimeToFill}d`,          color: '#f59e0b', icon: 'clock-outline' },
        ].map(s => (
          <View key={s.label} style={styles.kpiStripCard}>
            <MaterialCommunityIcons name={s.icon} size={20} color={s.color} />
            <Text style={[styles.kpiStripValue, { color: s.color }]}>{s.value}</Text>
            <Text style={styles.kpiStripLabel}>{s.label}</Text>
          </View>
        ))}
      </View>

      <TouchableOpacity style={styles.liveJobsBtn} onPress={() => openUrl('https://www.gov.uk/find-a-job')}>
        <MaterialCommunityIcons name="open-in-new" size={18} color="#fff" />
        <Text style={styles.liveJobsBtnText}>Browse Live UK Jobs</Text>
      </TouchableOpacity>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Top Hiring Sectors</Text>
        {data.jobMarket.topSectors.map(s => (
          <SectorBar key={s.name} sector={s} maxJobs={Math.max(...data.jobMarket.topSectors.map(x => x.jobs))} />
        ))}
      </View>

      <View style={styles.section}>
        <View style={styles.sectionHeader}>
          <Text style={styles.sectionTitle}>Featured Job Openings</Text>
          <TouchableOpacity onPress={() => openUrl('https://www.gov.uk/find-a-job')}>
            <Text style={styles.seeAll}>View all →</Text>
          </TouchableOpacity>
        </View>
        {data.jobMarket.recentJobs.map((job, i) => (
          <TouchableOpacity key={i} style={styles.jobCard} onPress={() => openUrl(job.url)}>
            <View style={styles.jobCardLeft}>
              <Text style={styles.jobTitle}>{job.title}</Text>
              <Text style={styles.jobCompany}>{job.company}</Text>
              <View style={styles.jobMetaRow}>
                <MaterialCommunityIcons name="map-marker-outline" size={13} color="#9ca3af" />
                <Text style={styles.jobLocation}>{job.location}</Text>
                {job.salary && (
                  <>
                    <Text style={styles.jobDivider}>·</Text>
                    <MaterialCommunityIcons name="currency-gbp" size={13} color="#10b981" />
                    <Text style={[styles.jobLocation, { color: '#10b981', fontWeight: '600' }]}>{job.salary.replace('£', '')}</Text>
                  </>
                )}
              </View>
            </View>
            <MaterialIcons name="open-in-new" size={18} color="#9ca3af" />
          </TouchableOpacity>
        ))}
      </View>
      <View style={{ height: 30 }} />
    </ScrollView>
  );

  // ─────────────────────────────────────────────────────────────────────────
  // TAB: SALARIES
  // ─────────────────────────────────────────────────────────────────────────
  const renderSalaries = () => (
    <ScrollView showsVerticalScrollIndicator={false}>
      <View style={styles.tabHeader}>
        <Text style={styles.tabTitle}>Engineering Salaries</Text>
        <Text style={styles.tabSubtitle}>UK averages · ONS 2024 data</Text>
      </View>

      <View style={styles.kpiStrip}>
        {[
          { label: 'UK Average',   value: `£${data.salaries.average.toLocaleString()}`, color: '#0e4e96', icon: 'currency-gbp' },
          { label: 'Salary Growth',value: `+${data.salaries.growth}%`,                  color: '#10b981', icon: 'trending-up' },
          { label: 'Top 10%',      value: `£${data.salaries.top10.toLocaleString()}`,   color: '#fdb913', icon: 'star' },
        ].map(s => (
          <View key={s.label} style={styles.kpiStripCard}>
            <MaterialCommunityIcons name={s.icon} size={20} color={s.color} />
            <Text style={[styles.kpiStripValue, { color: s.color }]}>{s.value}</Text>
            <Text style={styles.kpiStripLabel}>{s.label}</Text>
          </View>
        ))}
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Salary by Discipline</Text>
        {Object.entries(data.salaries.byDiscipline).map(([disc, avg], i) => {
          const maxAvg = Math.max(...Object.values(data.salaries.byDiscipline));
          const colors = ['#0e4e96', '#19abb5', '#fdb913', '#10b981', '#8b5cf6', '#f59e0b', '#ef4444'];
          const color  = colors[i % colors.length];
          return (
            <View key={disc} style={styles.salaryRow}>
              <Text style={styles.salaryDisc}>{disc}</Text>
              <View style={styles.salaryBarTrack}>
                <View style={[styles.salaryBarFill, { width: `${(avg / maxAvg) * 100}%`, backgroundColor: color }]}>
                  <Text style={styles.salaryBarValue}>£{(avg / 1000).toFixed(0)}k</Text>
                </View>
              </View>
            </View>
          );
        })}
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Salary by Experience</Text>
        {[
          { level: 'Entry (0-2y)',    range: '£28,000 – £38,000', avg: 33000 },
          { level: 'Mid (3-5y)',      range: '£38,000 – £55,000', avg: 46500 },
          { level: 'Senior (6-10y)', range: '£55,000 – £75,000', avg: 65000 },
          { level: 'Lead (10+y)',     range: '£75,000 – £120,000',avg: 97500 },
        ].map((l, i) => (
          <View key={i} style={styles.expSalaryCard}>
            <View style={[styles.expSalaryLevel, { backgroundColor: ['#0e4e96', '#19abb5', '#fdb913', '#10b981'][i] + '22' }]}>
              <Text style={[styles.expSalaryLevelText, { color: ['#0e4e96', '#19abb5', '#fdb913', '#10b981'][i] }]}>{l.level}</Text>
            </View>
            <Text style={styles.expSalaryRange}>{l.range}</Text>
          </View>
        ))}
      </View>

      <TouchableOpacity style={styles.salaryCompareBtn} onPress={() => navigation.navigate('SalaryComparison')}>
        <MaterialCommunityIcons name="scale-balance" size={18} color="#fff" />
        <Text style={styles.salaryCompareBtnText}>Open Full Salary Comparison Tool</Text>
        <MaterialIcons name="arrow-forward" size={18} color="#fff" />
      </TouchableOpacity>
      <View style={{ height: 30 }} />
    </ScrollView>
  );

  // ─────────────────────────────────────────────────────────────────────────
  // TAB: SKILLS
  // ─────────────────────────────────────────────────────────────────────────
  const renderSkills = () => (
    <ScrollView showsVerticalScrollIndicator={false}>
      <View style={styles.tabHeader}>
        <Text style={styles.tabTitle}>In-Demand Engineering Skills</Text>
        <Text style={styles.tabSubtitle}>Market demand & growth trends</Text>
      </View>
      {data.skills.map((sk, i) => (
        <View key={i} style={styles.skillCard}>
          <View style={styles.skillCardHeader}>
            <View style={[styles.skillCardIcon, { backgroundColor: sk.color + '22' }]}>
              <MaterialCommunityIcons name={sk.icon} size={22} color={sk.color} />
            </View>
            <View style={{ flex: 1 }}>
              <Text style={styles.skillCardName}>{sk.name}</Text>
              <Text style={styles.skillCardDemand}>Market demand: {sk.demand}%</Text>
            </View>
            <View style={[styles.skillGrowthBadge, { backgroundColor: '#10b98120' }]}>
              <MaterialCommunityIcons name="trending-up" size={12} color="#10b981" />
              <Text style={[styles.skillGrowthText]}>+{sk.growth}%</Text>
            </View>
          </View>
          <View style={styles.skillCardBar}>
            <View style={[styles.skillCardBarFill, { width: `${sk.demand * 4}%`, backgroundColor: sk.color }]} />
          </View>
          <TouchableOpacity style={styles.skillLearnBtn} onPress={() => openUrl('https://www.coursera.org')}>
            <Text style={styles.skillLearnText}>Find courses →</Text>
          </TouchableOpacity>
        </View>
      ))}
      <View style={{ height: 30 }} />
    </ScrollView>
  );

  // ─────────────────────────────────────────────────────────────────────────
  // TAB: EDUCATION
  // ─────────────────────────────────────────────────────────────────────────
  const renderEducation = () => (
    <ScrollView showsVerticalScrollIndicator={false}>
      <View style={styles.tabHeader}>
        <Text style={styles.tabTitle}>Education & Qualifications</Text>
        <Text style={styles.tabSubtitle}>UK engineering education landscape</Text>
      </View>

      <View style={styles.kpiStrip}>
        {[
          { label: 'Degree Holders',   value: '85%', color: '#0e4e96', icon: 'school' },
          { label: 'Certified Prof.',  value: '72%', color: '#19abb5', icon: 'certificate' },
          { label: 'Apprenticeships ↑',value: '+8.5%',color: '#10b981', icon: 'trending-up' },
        ].map(s => (
          <View key={s.label} style={styles.kpiStripCard}>
            <MaterialCommunityIcons name={s.icon} size={20} color={s.color} />
            <Text style={[styles.kpiStripValue, { color: s.color }]}>{s.value}</Text>
            <Text style={styles.kpiStripLabel}>{s.label}</Text>
          </View>
        ))}
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Top Engineering Universities</Text>
        {data.education.map((uni, i) => (
          <View key={i} style={styles.uniCard}>
            <View style={[styles.uniRankBadge, { backgroundColor: ['#fdb913', '#9ca3af', '#f59e0b'][Math.min(i, 2)] + '22' }]}>
              <Text style={styles.uniRankText}>{uni.rank}</Text>
            </View>
            <View style={styles.uniInfo}>
              <Text style={styles.uniName}>{uni.name}</Text>
              <Text style={styles.uniMeta}>{uni.enrolled} students · {uni.employment} employment</Text>
            </View>
          </View>
        ))}
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Professional Qualifications</Text>
        {[
          { qual: 'Chartered Engineer (CEng)', body: 'Engineering Council', benefit: '+15-25% salary premium', color: '#0e4e96' },
          { qual: 'Incorporated Engineer (IEng)', body: 'Engineering Council', benefit: '+8-15% salary premium', color: '#19abb5' },
          { qual: 'Engineering Technician (EngTech)', body: 'Engineering Council', benefit: 'Entry-level recognition', color: '#10b981' },
          { qual: 'Project Management Prof. (PMP)', body: 'PMI', benefit: '+10-20% salary increase', color: '#8b5cf6' },
        ].map((q, i) => (
          <View key={i} style={styles.qualCard}>
            <View style={[styles.qualDot, { backgroundColor: q.color }]} />
            <View style={{ flex: 1 }}>
              <Text style={styles.qualName}>{q.qual}</Text>
              <Text style={styles.qualBody}>{q.body}</Text>
            </View>
            <View style={[styles.qualBenefit, { backgroundColor: '#10b98120' }]}>
              <Text style={styles.qualBenefitText}>{q.benefit}</Text>
            </View>
          </View>
        ))}
      </View>
      <View style={{ height: 30 }} />
    </ScrollView>
  );

  // ─────────────────────────────────────────────────────────────────────────
  // TAB: REGIONAL
  // ─────────────────────────────────────────────────────────────────────────
  const renderRegional = () => (
    <ScrollView showsVerticalScrollIndicator={false}>
      <View style={styles.tabHeader}>
        <Text style={styles.tabTitle}>Regional Analysis</Text>
        <Text style={styles.tabSubtitle}>Engineering by UK region</Text>
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Regional Breakdown</Text>
        {data.regional.map((r, i) => (
          <TouchableOpacity key={r.region} style={[styles.regionDetailCard, selectedRegion === r.region && styles.regionDetailCardSelected]}
            onPress={() => setSelectedRegion(selectedRegion === r.region ? null : r.region)}>
            <View style={styles.regionDetailHeader}>
              <View style={styles.regionDetailLeft}>
                <Text style={styles.regionDetailName}>{r.region}</Text>
                <Text style={styles.regionDetailSector}>Top sector: {r.topSector}</Text>
              </View>
              <View style={[styles.regionDetailGrowth, { backgroundColor: '#10b98120' }]}>
                <Text style={[styles.regionDetailGrowthText, { color: '#10b981' }]}>+{r.growth}%</Text>
              </View>
            </View>
            {selectedRegion === r.region && (
              <View style={styles.regionExpanded}>
                {[
                  { label: 'Job Openings', value: r.jobs.toLocaleString(), icon: 'briefcase-outline', color: '#0e4e96' },
                  { label: 'Avg. Salary',  value: `£${r.avgSalary.toLocaleString()}`, icon: 'currency-gbp', color: '#10b981' },
                  { label: 'YoY Growth',   value: `+${r.growth}%`, icon: 'trending-up', color: '#fdb913' },
                ].map((stat, j) => (
                  <View key={j} style={styles.regionStatRow}>
                    <MaterialCommunityIcons name={stat.icon} size={16} color={stat.color} />
                    <Text style={styles.regionStatLabel}>{stat.label}</Text>
                    <Text style={[styles.regionStatValue, { color: stat.color }]}>{stat.value}</Text>
                  </View>
                ))}
                <TouchableOpacity style={styles.regionJobsBtn} onPress={() => openUrl(`https://www.gov.uk/find-a-job?q=engineering&l=${encodeURIComponent(r.region)}`)}>
                  <Text style={styles.regionJobsBtnText}>Find jobs in {r.region} →</Text>
                </TouchableOpacity>
              </View>
            )}
          </TouchableOpacity>
        ))}
      </View>
      <View style={{ height: 30 }} />
    </ScrollView>
  );

  // ─────────────────────────────────────────────────────────────────────────
  // TAB: NEWS
  // ─────────────────────────────────────────────────────────────────────────
  const renderNews = () => (
    <View style={{ flex: 1 }}>
      <View style={styles.newsSearchBar}>
        <MaterialIcons name="search" size={18} color="#9ca3af" />
        <TextInput style={styles.newsSearchInput} placeholder="Search articles..." value={searchQuery} onChangeText={setSearchQuery} placeholderTextColor="#9ca3af" />
      </View>
      <ScrollView showsVerticalScrollIndicator={false} refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor="#0e4e96" />}>
        <View style={styles.newsTabHeader}>
          <Text style={styles.tabTitle}>Engineering News</Text>
          <TouchableOpacity style={styles.allNewsBtn} onPress={() => navigation.navigate('NewsList')}>
            <Text style={styles.allNewsBtnText}>All News</Text>
            <MaterialIcons name="open-in-new" size={14} color="#0e4e96" />
          </TouchableOpacity>
        </View>
        {filteredNews.map((art, i) => (
          <TouchableOpacity key={art.id || i} style={styles.newsFullCard} onPress={() => navigation.navigate('NewsDetail', { newsItem: art })}>
            {art.imageUrl && <Image source={{ uri: art.imageUrl }} style={styles.newsFullImage} />}
            <View style={styles.newsFullBody}>
              <View style={styles.newsFullMeta}>
                <Text style={styles.newsFullSource}>{art.source}</Text>
                <Text style={styles.newsFullDate}>{formatDate(art.publishDate)}</Text>
              </View>
              <Text style={styles.newsFullTitle} numberOfLines={3}>{art.title}</Text>
              <Text style={styles.newsFullSnippet} numberOfLines={2}>{art.snippet}</Text>
              <View style={styles.newsFullFooter}>
                <Text style={styles.newsFullReadTime}>{art.readTime} min read</Text>
                <TouchableOpacity onPress={() => handleShare(art.title, art.snippet)}>
                  <MaterialCommunityIcons name="share-outline" size={16} color="#9ca3af" />
                </TouchableOpacity>
              </View>
            </View>
          </TouchableOpacity>
        ))}
        {filteredNews.length === 0 && !loading && (
          <View style={styles.emptyBox}>
            <MaterialCommunityIcons name="newspaper-variant-outline" size={40} color="#d1d5db" />
            <Text style={styles.emptyText}>No articles found</Text>
          </View>
        )}
        <View style={{ height: 30 }} />
      </ScrollView>
    </View>
  );

  // ─────────────────────────────────────────────────────────────────────────
  // TAB: AI INSIGHTS
  // ─────────────────────────────────────────────────────────────────────────
  const renderAI = () => (
    <ScrollView showsVerticalScrollIndicator={false}>
      <View style={styles.tabHeader}>
        <View style={styles.tabTitleRow}>
          <MaterialCommunityIcons name="brain" size={22} color="#0e4e96" />
          <Text style={styles.tabTitle}>Claude AI Insights</Text>
        </View>
        <Text style={styles.tabSubtitle}>Powered by Anthropic's Claude AI · Updated live</Text>
      </View>

      {aiLoading ? (
        <View style={styles.aiLoadingBox}>
          <ActivityIndicator size={40} color="#0e4e96" />
          <Text style={styles.aiLoadingText}>Generating insights with Claude AI...</Text>
          <Text style={styles.aiLoadingSubtext}>Analysing UK engineering market data</Text>
        </View>
      ) : aiInsights.length ? (
        <>
          {aiInsights.map((ins, i) => (
            <View key={i} style={styles.aiInsightCard}>
              <View style={[styles.aiInsightIcon, { backgroundColor: (ins.color || '#0e4e96') + '22' }]}>
                <MaterialCommunityIcons name={ins.icon || 'lightbulb-outline'} size={24} color={ins.color || '#0e4e96'} />
              </View>
              <View style={styles.aiInsightContent}>
                <Text style={styles.aiInsightTitle}>{ins.title}</Text>
                <Text style={styles.aiInsightText}>{ins.text}</Text>
              </View>
            </View>
          ))}
          <TouchableOpacity style={styles.aiRefreshBtn} onPress={() => { setAiInsights([]); loadAIInsights(); }}>
            <MaterialCommunityIcons name="refresh" size={16} color="#0e4e96" />
            <Text style={styles.aiRefreshText}>Regenerate Insights</Text>
          </TouchableOpacity>
        </>
      ) : (
        <View style={styles.aiEmptyBox}>
          <MaterialCommunityIcons name="robot-outline" size={50} color="#d1d5db" />
          <Text style={styles.aiEmptyText}>Loading AI analysis...</Text>
          <TouchableOpacity style={styles.aiGenerateBtn} onPress={loadAIInsights}>
            <Text style={styles.aiGenerateBtnText}>Generate Insights</Text>
          </TouchableOpacity>
        </View>
      )}

      <View style={styles.aiDataSummary}>
        <Text style={styles.aiDataSummaryTitle}>Data Used for Analysis</Text>
        {[
          `${data.jobMarket.totalJobs.toLocaleString()} active job openings`,
          `£${data.salaries.average.toLocaleString()} average salary`,
          `${data.jobMarket.growthRate}% year-on-year market growth`,
          `${data.skills.length} tracked engineering skill areas`,
          `${data.regional.length} UK regions analysed`,
        ].map((d, i) => (
          <View key={i} style={styles.aiDataRow}>
            <MaterialCommunityIcons name="check-circle" size={14} color="#10b981" />
            <Text style={styles.aiDataText}>{d}</Text>
          </View>
        ))}
      </View>
      <View style={{ height: 30 }} />
    </ScrollView>
  );

  // ─────────────────────────────────────────────────────────────────────────
  // ROOT RENDER
  // ─────────────────────────────────────────────────────────────────────────
  const renderTab = () => {
    switch (activeTab) {
      case 'overview':  return renderOverview();
      case 'jobs':      return renderJobs();
      case 'salaries':  return renderSalaries();
      case 'skills':    return renderSkills();
      case 'education': return renderEducation();
      case 'regional':  return renderRegional();
      case 'news':      return renderNews();
      case 'ai':        return renderAI();
      default:          return renderOverview();
    }
  };

  return (
    <SafeAreaView style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity style={styles.headerBtn} onPress={() => navigation.goBack()}>
          <MaterialIcons name="arrow-back" size={24} color="#0e4e96" />
        </TouchableOpacity>
        <View style={styles.logoRow}>
          <View style={styles.logoIcon}>
            <MaterialCommunityIcons name="engine" size={18} color="#fff" />
          </View>
          <View>
            <Text style={styles.logoText}>JOBENG</Text>
            <Text style={styles.logoSubText}>LIVE INSIGHTS</Text>
          </View>
        </View>
        <View style={styles.headerActions}>
          <View style={styles.liveIndicator}>
            <LiveDot live={isLive} />
            <Text style={styles.liveIndicatorText}>{isLive ? 'LIVE' : 'OFFLINE'}</Text>
          </View>
          <NewsAlertBadge alertCount={alertCount} onPress={() => setShowAlerts(true)} />
        </View>
      </View>

      {/* Tab bar */}
      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.tabBar} contentContainerStyle={{ paddingHorizontal: 12 }}>
        {tabs.map(t => (
          <TouchableOpacity key={t.id} style={[styles.tab, activeTab === t.id && styles.tabActive]} onPress={() => setActiveTab(t.id)}>
            <MaterialCommunityIcons name={t.icon} size={14} color={activeTab === t.id ? '#0e4e96' : '#9ca3af'} />
            <Text style={[styles.tabText, activeTab === t.id && styles.tabTextActive]}>{t.label}</Text>
            {t.id === 'ai' && <View style={styles.tabBetaBadge}><Text style={styles.tabBetaText}>AI</Text></View>}
          </TouchableOpacity>
        ))}
      </ScrollView>

      {/* Content */}
      {loading ? (
        <View style={styles.loadingBox}>
          <ActivityIndicator size={40} color="#0e4e96" />
          <Text style={styles.loadingText}>Loading UK engineering data...</Text>
        </View>
      ) : (
        <View style={{ flex: 1 }}>{renderTab()}</View>
      )}

      {/* Alert system */}
      <NewsAlertSystem
        visible={showAlerts}
        onClose={() => setShowAlerts(false)}
        onNavigateToNews={(art) => {
          setShowAlerts(false);
          if (art) navigation.navigate('NewsDetail', { newsItem: art });
          else setActiveTab('news');
        }}
      />
    </SafeAreaView>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// STYLES
// ─────────────────────────────────────────────────────────────────────────────
const styles = StyleSheet.create({
  container:      { flex: 1, backgroundColor: '#f8fafc' },
  // Header
  header:         { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 16, paddingVertical: 12, backgroundColor: '#fff', borderBottomWidth: 1, borderBottomColor: '#e5e7eb' },
  headerBtn:      { padding: 6 },
  logoRow:        { flex: 1, flexDirection: 'row', alignItems: 'center', marginLeft: 8, gap: 8 },
  logoIcon:       { width: 30, height: 30, borderRadius: 8, backgroundColor: '#0e4e96', justifyContent: 'center', alignItems: 'center' },
  logoText:       { fontSize: 15, fontWeight: '800', color: '#0e4e96', letterSpacing: 0.5 },
  logoSubText:    { fontSize: 9, color: '#19abb5', fontWeight: '700', letterSpacing: 1 },
  headerActions:  { flexDirection: 'row', alignItems: 'center', gap: 8 },
  liveIndicator:  { flexDirection: 'row', alignItems: 'center', gap: 4, backgroundColor: '#f0f4ff', paddingHorizontal: 8, paddingVertical: 4, borderRadius: 10 },
  liveDot:        { width: 7, height: 7, borderRadius: 4 },
  liveIndicatorText: { fontSize: 10, fontWeight: '700', color: '#0e4e96' },
  // Tab bar
  tabBar:         { backgroundColor: '#fff', borderBottomWidth: 1, borderBottomColor: '#e5e7eb', maxHeight: 50 },
  tab:            { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 14, paddingVertical: 12, gap: 5, borderBottomWidth: 2, borderBottomColor: 'transparent' },
  tabActive:      { borderBottomColor: '#0e4e96' },
  tabText:        { fontSize: 12, color: '#9ca3af', fontWeight: '600' },
  tabTextActive:  { color: '#0e4e96' },
  tabBetaBadge:   { backgroundColor: '#0e4e96', borderRadius: 4, paddingHorizontal: 4, paddingVertical: 1 },
  tabBetaText:    { fontSize: 8, color: '#fff', fontWeight: '800' },
  // Loading
  loadingBox:     { flex: 1, justifyContent: 'center', alignItems: 'center' },
  loadingText:    { marginTop: 14, fontSize: 14, color: '#6b7280' },
  // Live bar
  liveBar:        { flexDirection: 'row', alignItems: 'center', backgroundColor: '#fff', paddingHorizontal: 16, paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: '#f3f4f6', gap: 6 },
  liveText:       { fontSize: 11, color: '#6b7280', flex: 1 },
  refreshBtn:     { padding: 4 },
  // KPI grid
  kpiGrid:        { flexDirection: 'row', flexWrap: 'wrap', padding: 8, gap: 0 },
  dataCard:       { width: (width - 32) / 2, margin: 4, backgroundColor: '#fff', borderRadius: 12, padding: 14, borderLeftWidth: 3, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.05, shadowRadius: 3, elevation: 1 },
  dataCardTop:    { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 8 },
  dataCardIcon:   { width: 30, height: 30, borderRadius: 8, justifyContent: 'center', alignItems: 'center' },
  changeBadge:    { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 6, paddingVertical: 2, borderRadius: 8, gap: 2 },
  changeText:     { fontSize: 10, fontWeight: '700' },
  dataCardValue:  { fontSize: 20, fontWeight: '800', color: '#1f2937', marginBottom: 2 },
  dataCardTitle:  { fontSize: 11, color: '#9ca3af' },
  // Quick actions
  quickActionsGrid: { flexDirection: 'row', flexWrap: 'wrap', padding: 12, gap: 8 },
  quickAction:    { width: (width - 48) / 3, alignItems: 'center', gap: 6 },
  quickActionIcon:{ width: 48, height: 48, borderRadius: 14, justifyContent: 'center', alignItems: 'center' },
  quickActionLabel: { fontSize: 11, color: '#374151', fontWeight: '500', textAlign: 'center' },
  // Section
  section:        { backgroundColor: '#fff', marginVertical: 6, padding: 16 },
  sectionHeader:  { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 },
  sectionTitle:   { fontSize: 16, fontWeight: '700', color: '#1f2937' },
  seeAll:         { fontSize: 13, color: '#0e4e96', fontWeight: '600' },
  // Sector bar
  sectorRow:      { flexDirection: 'row', alignItems: 'center', marginBottom: 10, gap: 8 },
  sectorInfo:     { width: 100 },
  sectorName:     { fontSize: 12, fontWeight: '600', color: '#374151', marginBottom: 2 },
  sectorJobs:     { fontSize: 10, color: '#9ca3af' },
  sectorBarTrack: { flex: 1, height: 10, backgroundColor: '#f3f4f6', borderRadius: 5, overflow: 'hidden' },
  sectorBarFill:  { height: '100%', borderRadius: 5 },
  growthBadge:    { paddingHorizontal: 6, paddingVertical: 2, borderRadius: 8 },
  growthText:     { fontSize: 10, fontWeight: '700' },
  // Skills
  skillRow:       { flexDirection: 'row', alignItems: 'center', marginBottom: 12, gap: 10 },
  skillIcon:      { width: 36, height: 36, borderRadius: 10, justifyContent: 'center', alignItems: 'center' },
  skillName:      { fontSize: 13, fontWeight: '600', color: '#374151', marginBottom: 4 },
  skillBarTrack:  { height: 6, backgroundColor: '#f3f4f6', borderRadius: 3, overflow: 'hidden' },
  skillBarFill:   { height: '100%', borderRadius: 3 },
  skillDemand:    { fontSize: 13, fontWeight: '700', color: '#1f2937' },
  skillGrowth:    { fontSize: 10, fontWeight: '600' },
  // News card (overview)
  newsCard:       { flexDirection: 'row', borderBottomWidth: 1, borderBottomColor: '#f3f4f6', paddingVertical: 12, gap: 10 },
  newsCardContent:{ flex: 1 },
  newsCardMeta:   { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 4 },
  newsCardSource: { fontSize: 10, fontWeight: '700', color: '#0e4e96', textTransform: 'uppercase' },
  newsCardDate:   { fontSize: 10, color: '#9ca3af' },
  newsCardTitle:  { fontSize: 14, fontWeight: '600', color: '#1f2937', lineHeight: 19, marginBottom: 4 },
  newsCardSnippet:{ fontSize: 12, color: '#6b7280', lineHeight: 17 },
  newsCardThumb:  { width: 70, height: 70, borderRadius: 8 },
  // Region cards (overview)
  regionCard:     { width: 130, backgroundColor: '#fff', borderRadius: 12, padding: 12, marginRight: 10, borderWidth: 1, borderColor: '#e5e7eb' },
  regionName:     { fontSize: 14, fontWeight: '700', color: '#1f2937', marginBottom: 6 },
  regionJobs:     { fontSize: 12, color: '#0e4e96', fontWeight: '600', marginBottom: 2 },
  regionSalary:   { fontSize: 11, color: '#6b7280', marginBottom: 6 },
  regionGrowth:   { paddingHorizontal: 8, paddingVertical: 3, borderRadius: 8, marginBottom: 6, alignSelf: 'flex-start' },
  regionGrowthText: { fontSize: 11, fontWeight: '700' },
  regionSector:   { fontSize: 10, color: '#9ca3af' },
  // Tab header
  tabHeader:      { backgroundColor: '#fff', padding: 16, borderBottomWidth: 1, borderBottomColor: '#f3f4f6' },
  tabTitleRow:    { flexDirection: 'row', alignItems: 'center', gap: 8 },
  tabTitle:       { fontSize: 18, fontWeight: '700', color: '#1f2937', marginBottom: 2 },
  tabSubtitle:    { fontSize: 12, color: '#9ca3af' },
  // KPI strip
  kpiStrip:       { flexDirection: 'row', backgroundColor: '#fff', padding: 12, marginBottom: 6, gap: 8 },
  kpiStripCard:   { flex: 1, alignItems: 'center', paddingVertical: 10, backgroundColor: '#f8fafc', borderRadius: 10, gap: 4 },
  kpiStripValue:  { fontSize: 16, fontWeight: '700' },
  kpiStripLabel:  { fontSize: 10, color: '#9ca3af', textAlign: 'center' },
  // Live jobs btn
  liveJobsBtn:    { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', backgroundColor: '#0e4e96', margin: 12, paddingVertical: 14, borderRadius: 12, gap: 8 },
  liveJobsBtnText:{ color: '#fff', fontWeight: '700', fontSize: 15 },
  // Job card
  jobCard:        { flexDirection: 'row', alignItems: 'center', paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: '#f3f4f6' },
  jobCardLeft:    { flex: 1 },
  jobTitle:       { fontSize: 14, fontWeight: '700', color: '#1f2937', marginBottom: 3 },
  jobCompany:     { fontSize: 13, color: '#0e4e96', fontWeight: '500', marginBottom: 4 },
  jobMetaRow:     { flexDirection: 'row', alignItems: 'center', gap: 4 },
  jobLocation:    { fontSize: 12, color: '#6b7280' },
  jobDivider:     { fontSize: 12, color: '#d1d5db' },
  // Salary
  salaryRow:      { marginBottom: 10 },
  salaryDisc:     { fontSize: 12, fontWeight: '500', color: '#374151', marginBottom: 4 },
  salaryBarTrack: { height: 32, backgroundColor: '#f3f4f6', borderRadius: 8, overflow: 'hidden' },
  salaryBarFill:  { height: '100%', borderRadius: 8, justifyContent: 'center', paddingHorizontal: 10, minWidth: 50 },
  salaryBarValue: { color: '#fff', fontSize: 11, fontWeight: '700' },
  expSalaryCard:  { flexDirection: 'row', alignItems: 'center', paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: '#f3f4f6', gap: 10 },
  expSalaryLevel: { paddingHorizontal: 10, paddingVertical: 5, borderRadius: 10 },
  expSalaryLevelText: { fontSize: 12, fontWeight: '700' },
  expSalaryRange: { fontSize: 13, color: '#374151', fontWeight: '500' },
  salaryCompareBtn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', backgroundColor: '#0e4e96', margin: 12, paddingVertical: 14, borderRadius: 12, gap: 8 },
  salaryCompareBtnText: { color: '#fff', fontWeight: '700', fontSize: 15 },
  // Skill card
  skillCard:      { backgroundColor: '#fff', marginHorizontal: 12, marginBottom: 8, borderRadius: 12, padding: 14 },
  skillCardHeader:{ flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 10 },
  skillCardIcon:  { width: 42, height: 42, borderRadius: 12, justifyContent: 'center', alignItems: 'center' },
  skillCardName:  { fontSize: 15, fontWeight: '700', color: '#1f2937' },
  skillCardDemand:{ fontSize: 12, color: '#9ca3af', marginTop: 2 },
  skillGrowthBadge: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 8, paddingVertical: 4, borderRadius: 10, backgroundColor: '#10b98120', gap: 3 },
  skillGrowthText:{ fontSize: 11, color: '#10b981', fontWeight: '700' },
  skillCardBar:   { height: 8, backgroundColor: '#f3f4f6', borderRadius: 4, overflow: 'hidden', marginBottom: 10 },
  skillCardBarFill: { height: '100%', borderRadius: 4 },
  skillLearnBtn:  { alignSelf: 'flex-end' },
  skillLearnText: { fontSize: 12, color: '#0e4e96', fontWeight: '600' },
  // Education
  uniCard:        { flexDirection: 'row', alignItems: 'center', paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: '#f3f4f6', gap: 12 },
  uniRankBadge:   { width: 48, height: 32, borderRadius: 8, justifyContent: 'center', alignItems: 'center' },
  uniRankText:    { fontSize: 11, fontWeight: '700', color: '#374151' },
  uniInfo:        { flex: 1 },
  uniName:        { fontSize: 14, fontWeight: '600', color: '#1f2937', marginBottom: 2 },
  uniMeta:        { fontSize: 12, color: '#9ca3af' },
  qualCard:       { flexDirection: 'row', alignItems: 'center', paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: '#f3f4f6', gap: 10 },
  qualDot:        { width: 10, height: 10, borderRadius: 5 },
  qualName:       { fontSize: 13, fontWeight: '600', color: '#1f2937', marginBottom: 2 },
  qualBody:       { fontSize: 11, color: '#9ca3af' },
  qualBenefit:    { paddingHorizontal: 8, paddingVertical: 4, borderRadius: 8 },
  qualBenefitText:{ fontSize: 10, color: '#10b981', fontWeight: '600' },
  // Regional
  regionDetailCard: { backgroundColor: '#fff', marginHorizontal: 12, marginBottom: 8, borderRadius: 12, padding: 14, borderWidth: 1, borderColor: '#e5e7eb' },
  regionDetailCardSelected: { borderColor: '#0e4e96', backgroundColor: '#f0f4ff' },
  regionDetailHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  regionDetailLeft: {},
  regionDetailName: { fontSize: 15, fontWeight: '700', color: '#1f2937', marginBottom: 3 },
  regionDetailSector: { fontSize: 12, color: '#9ca3af' },
  regionDetailGrowth: { paddingHorizontal: 10, paddingVertical: 4, borderRadius: 10 },
  regionDetailGrowthText: { fontSize: 12, fontWeight: '700' },
  regionExpanded: { marginTop: 12, borderTopWidth: 1, borderTopColor: '#e5e7eb', paddingTop: 12 },
  regionStatRow:  { flexDirection: 'row', alignItems: 'center', gap: 8, paddingVertical: 6 },
  regionStatLabel:{ flex: 1, fontSize: 13, color: '#6b7280' },
  regionStatValue:{ fontSize: 13, fontWeight: '700' },
  regionJobsBtn:  { marginTop: 10, paddingVertical: 10, alignItems: 'center', backgroundColor: '#0e4e96', borderRadius: 8 },
  regionJobsBtnText: { color: '#fff', fontWeight: '700', fontSize: 13 },
  // News tab
  newsSearchBar:  { flexDirection: 'row', alignItems: 'center', backgroundColor: '#fff', paddingHorizontal: 16, paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: '#e5e7eb', gap: 8 },
  newsSearchInput:{ flex: 1, fontSize: 14, color: '#374151', height: 36 },
  newsTabHeader:  { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', padding: 16, backgroundColor: '#fff' },
  allNewsBtn:     { flexDirection: 'row', alignItems: 'center', gap: 4, backgroundColor: '#f0f4ff', paddingHorizontal: 12, paddingVertical: 6, borderRadius: 8 },
  allNewsBtnText: { fontSize: 12, color: '#0e4e96', fontWeight: '600' },
  newsFullCard:   { backgroundColor: '#fff', marginBottom: 6 },
  newsFullImage:  { width: '100%', height: 180 },
  newsFullBody:   { padding: 16 },
  newsFullMeta:   { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 6 },
  newsFullSource: { fontSize: 11, fontWeight: '700', color: '#0e4e96', textTransform: 'uppercase' },
  newsFullDate:   { fontSize: 11, color: '#9ca3af' },
  newsFullTitle:  { fontSize: 16, fontWeight: '700', color: '#1f2937', lineHeight: 22, marginBottom: 6 },
  newsFullSnippet:{ fontSize: 13, color: '#6b7280', lineHeight: 19, marginBottom: 10 },
  newsFullFooter: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  newsFullReadTime:{ fontSize: 11, color: '#9ca3af' },
  emptyBox:       { alignItems: 'center', paddingVertical: 40 },
  emptyText:      { fontSize: 14, color: '#9ca3af', marginTop: 10 },
  // AI
  aiLoadingBox:   { alignItems: 'center', paddingVertical: 60, paddingHorizontal: 30 },
  aiLoadingText:  { marginTop: 16, fontSize: 16, fontWeight: '600', color: '#1f2937' },
  aiLoadingSubtext: { fontSize: 13, color: '#9ca3af', marginTop: 4 },
  aiInsightCard:  { flexDirection: 'row', alignItems: 'flex-start', backgroundColor: '#fff', margin: 12, marginBottom: 6, borderRadius: 14, padding: 16, gap: 12, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.05, shadowRadius: 3, elevation: 1 },
  aiInsightIcon:  { width: 46, height: 46, borderRadius: 14, justifyContent: 'center', alignItems: 'center' },
  aiInsightContent: { flex: 1 },
  aiInsightTitle: { fontSize: 15, fontWeight: '700', color: '#1f2937', marginBottom: 4 },
  aiInsightText:  { fontSize: 13, color: '#374151', lineHeight: 20 },
  aiRefreshBtn:   { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6, margin: 16, paddingVertical: 12, backgroundColor: '#f0f4ff', borderRadius: 10, borderWidth: 1, borderColor: '#0e4e96' },
  aiRefreshText:  { color: '#0e4e96', fontWeight: '600', fontSize: 14 },
  aiEmptyBox:     { alignItems: 'center', paddingVertical: 60 },
  aiEmptyText:    { fontSize: 14, color: '#9ca3af', marginTop: 16, marginBottom: 20 },
  aiGenerateBtn:  { backgroundColor: '#0e4e96', paddingHorizontal: 24, paddingVertical: 12, borderRadius: 12 },
  aiGenerateBtnText: { color: '#fff', fontWeight: '700', fontSize: 15 },
  aiDataSummary:  { margin: 12, backgroundColor: '#fff', borderRadius: 14, padding: 16 },
  aiDataSummaryTitle: { fontSize: 14, fontWeight: '700', color: '#1f2937', marginBottom: 10 },
  aiDataRow:      { flexDirection: 'row', alignItems: 'center', gap: 8, paddingVertical: 5 },
  aiDataText:     { fontSize: 13, color: '#374151' },
});
